import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './interventions.css';

const InterventionDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState('alerts');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/interventions/dashboard/', {
                withCredentials: true
            });
            
            if (response.data.success) {
                setDashboardData(response.data.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Error loading dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleAlertAction = async (alertId, action, additionalData = {}) => {
        try {
            const response = await axios.post(`/api/interventions/alerts/${alertId}/action/`, {
                action,
                ...additionalData
            }, {
                withCredentials: true
            });

            if (response.data.success) {
                // Refresh dashboard data
                fetchDashboardData();
            } else {
                alert('Failed to perform action');
            }
        } catch (err) {
            console.error('Error performing alert action:', err);
            alert('Error performing action');
        }
    };

    const getSeverityColor = (severity) => {
        const colors = {
            'low': '#10B981',
            'medium': '#F59E0B',
            'high': '#F97316',
            'critical': '#EF4444'
        };
        return colors[severity] || '#6B7280';
    };

    const getRiskLevelColor = (riskLevel) => {
        const colors = {
            'low': '#10B981',
            'moderate': '#F59E0B',
            'high': '#F97316',
            'critical': '#EF4444'
        };
        return colors[riskLevel] || '#6B7280';
    };

    if (loading) {
        return (
            <div className="intervention-dashboard">
                <div className="loading-spinner">Loading intervention dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="intervention-dashboard">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div className="intervention-dashboard">
            <div className="dashboard-header">
                <h1>Intervention Dashboard</h1>
                <div className="dashboard-stats">
                    <div className="stat-card">
                        <div className="stat-number">{dashboardData.stats.active_alerts_count}</div>
                        <div className="stat-label">Active Alerts</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{dashboardData.stats.open_cases_count}</div>
                        <div className="stat-label">Open Cases</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{dashboardData.stats.high_risk_students_count}</div>
                        <div className="stat-label">High Risk Students</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{dashboardData.stats.todays_alerts_count}</div>
                        <div className="stat-label">Today's Alerts</div>
                    </div>
                </div>
            </div>

            <div className="dashboard-tabs">
                <button 
                    className={`tab-button ${selectedTab === 'alerts' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('alerts')}
                >
                    My Alerts ({dashboardData.my_alerts.length})
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'cases' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('cases')}
                >
                    My Cases ({dashboardData.my_cases.length})
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'risk' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('risk')}
                >
                    High Risk Students ({dashboardData.high_risk_students.length})
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'today' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('today')}
                >
                    Today's Alerts ({dashboardData.todays_alerts.length})
                </button>
            </div>

            <div className="dashboard-content">
                {selectedTab === 'alerts' && (
                    <div className="alerts-section">
                        <h2>My Assigned Alerts</h2>
                        {dashboardData.my_alerts.length === 0 ? (
                            <div className="empty-state">No alerts assigned to you</div>
                        ) : (
                            <div className="alerts-list">
                                {dashboardData.my_alerts.map(alert => (
                                    <AlertCard 
                                        key={alert.id} 
                                        alert={alert} 
                                        onAction={handleAlertAction}
                                        getSeverityColor={getSeverityColor}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {selectedTab === 'cases' && (
                    <div className="cases-section">
                        <h2>My Cases</h2>
                        {dashboardData.my_cases.length === 0 ? (
                            <div className="empty-state">No cases assigned to you</div>
                        ) : (
                            <div className="cases-list">
                                {dashboardData.my_cases.map(case_ => (
                                    <CaseCard 
                                        key={case_.id} 
                                        case={case_} 
                                        getSeverityColor={getSeverityColor}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {selectedTab === 'risk' && (
                    <div className="risk-section">
                        <h2>High Risk Students</h2>
                        {dashboardData.high_risk_students.length === 0 ? (
                            <div className="empty-state">No high risk students</div>
                        ) : (
                            <div className="risk-list">
                                {dashboardData.high_risk_students.map(profile => (
                                    <RiskProfileCard 
                                        key={profile.id} 
                                        profile={profile} 
                                        getRiskLevelColor={getRiskLevelColor}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {selectedTab === 'today' && (
                    <div className="todays-alerts-section">
                        <h2>Today's New Alerts</h2>
                        {dashboardData.todays_alerts.length === 0 ? (
                            <div className="empty-state">No new alerts today</div>
                        ) : (
                            <div className="alerts-list">
                                {dashboardData.todays_alerts.map(alert => (
                                    <AlertCard 
                                        key={alert.id} 
                                        alert={alert} 
                                        onAction={handleAlertAction}
                                        getSeverityColor={getSeverityColor}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const AlertCard = ({ alert, onAction, getSeverityColor }) => {
    const [showActions, setShowActions] = useState(false);

    return (
        <div className="alert-card">
            <div className="alert-header">
                <div className="alert-title">
                    <span 
                        className="severity-badge"
                        style={{ backgroundColor: getSeverityColor(alert.severity) }}
                    >
                        {alert.severity.toUpperCase()}
                    </span>
                    <h3>{alert.title}</h3>
                </div>
                <div className="alert-meta">
                    <span className="student-name">{alert.student.full_name}</span>
                    <span className="trigger-date">
                        {new Date(alert.trigger_date).toLocaleDateString()}
                    </span>
                </div>
            </div>
            
            <div className="alert-description">
                {alert.description}
            </div>

            <div className="alert-actions">
                <button 
                    className="action-btn primary"
                    onClick={() => setShowActions(!showActions)}
                >
                    Actions
                </button>
                
                {showActions && (
                    <div className="action-menu">
                        <button 
                            className="action-btn"
                            onClick={() => onAction(alert.id, 'acknowledge')}
                        >
                            Acknowledge
                        </button>
                        <button 
                            className="action-btn"
                            onClick={() => onAction(alert.id, 'create_case')}
                        >
                            Create Case
                        </button>
                        <button 
                            className="action-btn"
                            onClick={() => {
                                const notes = prompt('Resolution notes:');
                                if (notes) {
                                    onAction(alert.id, 'resolve', { resolution_notes: notes });
                                }
                            }}
                        >
                            Resolve
                        </button>
                        <button 
                            className="action-btn danger"
                            onClick={() => {
                                const reason = prompt('Dismissal reason:');
                                if (reason) {
                                    onAction(alert.id, 'dismiss', { dismissal_reason: reason });
                                }
                            }}
                        >
                            Dismiss
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const CaseCard = ({ case: case_, getSeverityColor }) => {
    return (
        <div className="case-card">
            <div className="case-header">
                <div className="case-title">
                    <span 
                        className="priority-badge"
                        style={{ backgroundColor: getSeverityColor(case_.priority) }}
                    >
                        {case_.priority.toUpperCase()}
                    </span>
                    <h3>{case_.title}</h3>
                </div>
                <div className="case-meta">
                    <span className="student-name">{case_.student.full_name}</span>
                    <span className="case-type">{case_.case_type}</span>
                </div>
            </div>
            
            <div className="case-description">
                {case_.description}
            </div>

            <div className="case-stats">
                <div className="stat">
                    <span className="stat-label">Days Open:</span>
                    <span className="stat-value">{case_.days_open}</span>
                </div>
                <div className="stat">
                    <span className="stat-label">Actions:</span>
                    <span className="stat-value">{case_.completed_action_count}/{case_.action_count}</span>
                </div>
                {case_.overdue_action_count > 0 && (
                    <div className="stat overdue">
                        <span className="stat-label">Overdue:</span>
                        <span className="stat-value">{case_.overdue_action_count}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const RiskProfileCard = ({ profile, getRiskLevelColor }) => {
    return (
        <div className="risk-profile-card">
            <div className="profile-header">
                <div className="student-info">
                    <h3>{profile.student.full_name}</h3>
                    <span className="grade-level">Grade {profile.student.grade_level}</span>
                </div>
                <div className="risk-level">
                    <span 
                        className="risk-badge"
                        style={{ backgroundColor: getRiskLevelColor(profile.overall_risk_level) }}
                    >
                        {profile.overall_risk_level.toUpperCase()} RISK
                    </span>
                </div>
            </div>

            <div className="risk-metrics">
                <div className="metric">
                    <span className="metric-label">Attendance Rate (30 days):</span>
                    <span className="metric-value">{profile.attendance_rate_30_days}%</span>
                </div>
                <div className="metric">
                    <span className="metric-label">Consecutive Absences:</span>
                    <span className="metric-value">{profile.consecutive_absences}</span>
                </div>
                <div className="metric">
                    <span className="metric-label">Alerts (30 days):</span>
                    <span className="metric-value">{profile.total_alerts_30_days}</span>
                </div>
                <div className="metric">
                    <span className="metric-label">Active Cases:</span>
                    <span className="metric-value">{profile.active_cases_count}</span>
                </div>
            </div>

            <div className="risk-factors">
                <h4>Risk Factors:</h4>
                <ul>
                    {profile.risk_summary.map((factor, index) => (
                        <li key={index}>{factor}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default InterventionDashboard;
